import { newsAPI } from './api';

// Получение click_id из URL с поддержкой всех возможных форматов
export const getAdClickIdFromUrl = () => {
    // 1. Сначала проверяем URL-параметры
    const urlParams = new URLSearchParams(window.location.search);
    const clickId = urlParams.get('click_id');
    
    if (clickId) {
        return clickId;
    }
    
    // 2. Проверяем полный URL для всех возможных паттернов
    const fullUrl = window.location.href;
    
    // Специальные паттерны для разных форматов URL
    const patterns = [
        // Паттерн для URL типа: /news/slug?click_id=xxx
        /\/news\/[^?]+\?click_id=([^&]+)/,
        // Общий паттерн для параметра click_id в любом месте URL
        /[?&]click_id=([^&]+)/,
        // Паттерн для click_id в конце URL после последнего /
        /\/([a-f0-9]{6,}(?:-[a-f0-9]+)*)$/i,
        // Паттерн для click_id в конце URL без дефисов
        /\/([a-f0-9]{20,40})$/i
    ];
    
    // Проверяем каждый паттерн
    for (const pattern of patterns) {
        const match = fullUrl.match(pattern);
        if (match && match[1]) {
            return match[1];
        }
    }
    
    // 3. Проверяем referrer URL, если текущий URL не содержит click_id
    if (document.referrer) {
        try {
            // Пробуем найти click_id в URL referrer
            const referrerUrl = document.referrer;
            
            // Проверяем те же паттерны в referrer URL
            for (const pattern of patterns) {
                const match = referrerUrl.match(pattern);
                if (match && match[1]) {
                    return match[1];
                }
            }
            
            // Пробуем получить параметры из referrer URL
            const referrerObj = new URL(referrerUrl);
            const referrerParams = new URLSearchParams(referrerObj.search);
            const referrerClickId = referrerParams.get('click_id');
            
            if (referrerClickId) {
                return referrerClickId;
            }
        } catch (e) {
            // Ошибка анализа referrer игнорируется
        }
    }
    
    // 4. Если ничего не нашли, проверяем хеш в URL (иногда параметры могут быть там)
    if (window.location.hash) {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const hashClickId = hashParams.get('click_id');
        
        if (hashClickId) {
            return hashClickId;
        }
    }
    
    return null;
};

// Регистрация просмотра
export const registerView = async (newsId) => {
    if (!newsId) return;
    
    try {
        await newsAPI.registerView(newsId);
    } catch (error) {
        console.error('Error registering view:', error);
    }
};

// Регистрация клика
export const registerClick = async (newsId) => {
    if (!newsId) return;
    
    try {
        // 1. Сначала пробуем получить click_id из URL
        let adClickId = getAdClickIdFromUrl();
        
        // 2. Если в URL не найден, пробуем взять из localStorage
        if (!adClickId) {
            adClickId = localStorage.getItem('adClickId');
        }
        
        // 3. Также проверяем cookie
        if (!adClickId) {
            const cookies = document.cookie.split(';').map(cookie => cookie.trim());
            const clickIdCookie = cookies.find(cookie => cookie.startsWith('adClickId='));
            if (clickIdCookie) {
                adClickId = clickIdCookie.split('=')[1];
            }
        }
        
        // 4. Если все равно не нашли, но есть параметр в URL - пробуем другие возможные имена параметров
        if (!adClickId) {
            const urlParams = new URLSearchParams(window.location.search);
            const possibleParamNames = ['aff_id', 'affid', 'affiliate_id', 'aff', 'subid', 'sub_id', 'tid', 'tracking_id'];
            
            for (const paramName of possibleParamNames) {
                const paramValue = urlParams.get(paramName);
                if (paramValue) {
                    adClickId = paramValue;
                    break;
                }
            }
        }
        
        // 5. В крайнем случае, создаем свой click_id на основе timestamp и случайного числа
        if (!adClickId) {
            const timestamp = new Date().getTime();
            const random = Math.floor(Math.random() * 1000000);
            adClickId = `gen_${timestamp}_${random}`;
            
            // Сохраняем его для дальнейшего использования
            localStorage.setItem('adClickId', adClickId);
            
            // И в cookie
            const expiryDate = new Date();
            expiryDate.setDate(expiryDate.getDate() + 30);
            document.cookie = `adClickId=${adClickId}; expires=${expiryDate.toUTCString()}; path=/`;
        }
        
        // Явно передаем click_id и в теле запроса, и как query-параметр для надежности
        const response = await newsAPI.registerClick(newsId, adClickId);
        return response;
    } catch (error) {
        console.error('Error registering click:', error);
        return null;
    }
};

// Регистрация просмотра рекламы
export const registerAdView = async (newsId) => {
    if (!newsId) return;
    
    try {
        await newsAPI.registerAdView(newsId);
    } catch (error) {
        console.error('Error registering ad view:', error);
    }
};

// Регистрация клика по рекламе
export const registerAdClick = async (newsId) => {
    if (!newsId) return;
    
    try {
        await newsAPI.registerAdClick(newsId);
    } catch (error) {
        console.error('Error registering ad click:', error);
    }
};

// Обработка клика с редиректом
export const handleNewsClick = async (newsId, slug, sourceUrl) => {
    if (!newsId || !slug) return false;

    try {
        // Получаем click_id из URL или других источников
        const adClickId = getAdClickIdFromUrl();
        
        await registerClick(newsId);
        const response = await newsAPI.getRedirectUrl(slug, adClickId);
        
        if (response.data?.redirectUrl) {
            window.open(response.data.redirectUrl, '_blank', 'noopener,noreferrer');
            return true;
        }
    } catch (error) {
        if (sourceUrl) {
            let url = sourceUrl;
            if (!url.startsWith('http')) {
                url = 'https://' + url;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
            return true;
        }
    }
    return false;
};

// Обработка клика по рекламе с редиректом
export const handleAdClick = async (newsIdOrNews, slugParam, sourceUrl) => {
    try {
        // Определяем newsId и slug в зависимости от формата входных данных
        let newsId, slug;
        
        // Проверяем, получили ли мы объект news или отдельные параметры
        if (typeof newsIdOrNews === 'object' && newsIdOrNews !== null) {
            // Если это объект, извлекаем id и slug из него
            newsId = newsIdOrNews.id;
            slug = newsIdOrNews.slug;
        } else {
            // Если получены отдельные параметры
            newsId = newsIdOrNews;
            slug = slugParam;
        }
        
        // Проверяем, что у нас есть необходимые данные
        if (!newsId || !slug) {
            return false;
        }
        
        // Получаем click_id из URL или других источников
        const adClickId = getAdClickIdFromUrl();
        
        // Регистрируем клик
        await registerAdClick(newsId);
        
        // Получаем URL перенаправления
        const response = await newsAPI.getAdRedirectUrl(slug, adClickId);
        
        if (response.data?.redirectUrl) {
            window.open(response.data.redirectUrl, '_blank', 'noopener,noreferrer');
            return true;
        } else if (response.data?.url) {
            // Для обратной совместимости
            window.open(response.data.url, '_blank', 'noopener,noreferrer');
            return true;
        }
    } catch (error) {
        console.error('Error handling ad click:', error);
        if (sourceUrl) {
            let url = sourceUrl;
            if (!url.startsWith('http')) {
                url = 'https://' + url;
            }
            window.open(url, '_blank', 'noopener,noreferrer');
            return true;
        }
    }
    return false;
}; 